import React, { useEffect, useState } from "react";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Container,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { formatDateTimes } from "../../../Components/Common/FormatDate";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";
import { exportToExcel } from "../../../utilis/exportToExcel";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const TransactionReport = () => {
  const { id } = useParams();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);
  const [productData, setProductData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [productName, setProductName] = useState("");
  console.log("🚀 ~ TransactionReport ~ filterText:", filterText);

  // Summary totals (for ALL data, not just current page)
  const [summaryTotals, setSummaryTotals] = useState({
    totalQuantity: 0,
    totalDebit: 0,
    totalCredit: 0,
    totalAmount: 0,
  });

  // Export modal states
  const [exportModal, setExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  // Format helper
  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  const navigate = useNavigate();
  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  // Fetch product list
  const fetchItemList = async () => {
    try {
      setLoading(true);
      const response = await PaymentTypeServices.getList();
      setProductData(response || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemList();
  }, []);

  // Fetch summary totals for ALL data (not paginated)
  const fetchSummaryTotals = async (page = currentPage, size = pageSize) => {
    if (!fromDate || !toDate) return;

    try {
      const response = await SalesReportService.getTransactionList({
        OrderStatus: selectedOrderStatus?.value ?? "",
        Salesperson: filterText,
        PaymentMethodId: selectedCategory?.value ?? "",
        ProductName: productName,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
        page,
        pageSize: size, // fetch all for summary
      });

      setSummaryTotals(response?.summary || {});
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch transaction data (paginated)
  const fetchData = async (page = currentPage, size = pageSize) => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    try {
      const response = await SalesReportService.getTransactionList({
        OrderStatus: selectedOrderStatus?.value ?? "",
        Salesperson: filterText,
        PaymentMethodId: selectedCategory?.value ?? "",
        ProductName: productName,
        fromDate: formatDate(fromDate),
        toDate: formatDate(toDate),
        page,
        pageSize: size,
      });

      setAllData(response?.items || []);
      setTotalRows(response?.totalCount || 0);

      fetchSummaryTotals();
    }
   catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "60px",
    },
    {
      name: "Invoice No",
      selector: (row) => row.invoiceNo,
      cell: (row) => (
        <Link
          to={`/order/${row?.invoiceNo}`}
          title={`${row?.invoiceNo}`}
          target="_blank"
          className="cursor-pointer text-truncate"
        >
          {row.invoiceNo}
        </Link>
      ),
      sortable: true,
    },
    {
      name: "Customer",
      selector: (row) => row.customerName,
      cell: (row) => (
        <span
          title={`${row?.customerName}`}
          className="cursor-pointer text-truncate"
        >
          {row.customerName || "N/A"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Txn Date",
      selector: (row) => formatDateTimes(row?.transactionDate),
      cell: (row) => (
        <span
          title={`${formatDateTimes(row?.transactionDate)}`}
          className="cursor-pointer text-truncate"
        >
          {formatDateTimes(row.transactionDate)}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Payment Ref",
      selector: (row) => row.paymentReference,
      cell: (row) => (
        <span
          title={`${row?.paymentReference}`}
          className="cursor-pointer text-truncate"
        >
          {row.paymentReference}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Method",
      selector: (row) => row.paymentMethod,
      sortable: true,
    },

    {
      name: "Item",
      selector: (row) => row.itemCount,
      sortable: true,
      right: true,
      width: "90px",
    },
    {
      name: "Discount",
      selector: (row) => row.discount,
      sortable: true,
      right: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span
          className={`badge px-2 py-1 text-xs rounded-full ${
            row.status === "Completed"
              ? "bg-success"
              : row.status === "Pending"
              ? "bg-warning"
              : "bg-danger"
          }`}
        >
          {row.status}
        </span>
      ),
    },

    {
      name: "Service",
      selector: (row) => row.serviceCharge,
      cell: (row) => <NumberWithCommas number={row.serviceCharge} />,
      sortable: true,
      right: true,
    },
    {
      name: "Tax",
      selector: (row) => row.tax,
      cell: (row) => <NumberWithCommas number={row.tax} />,
      sortable: true,
      right: true,
    },
    {
      name: "Gross Amt",
      selector: (row) => row.grossAmount,
      sortable: true,
      right: true,
      cell: (row) => <NumberWithCommas number={row.grossAmount} />,
    },
    {
      name: "Net Amt",
      selector: (row) => row.netAmount,
      cell: (row) => <NumberWithCommas number={row.netAmount} />,
      sortable: true,
      right: true,
    },
  ];
  const orderStatus = [
    { value: 2, label: "Completed" },
    { value: 1, label: "Ongoing" },
    { value: 3, label: "Cancelled" },
    { value: 4, label: "Preparing" },
  ];
  const orderStatusOptions =
    orderStatus?.map((item) => ({
      value: item.value,
      label: item.label,
    })) || [];
  const categoryOptions =
    productData?.map((item) => ({
      value: item.id,
      label: item.name,
    })) || [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, pageSize);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
    fetchData(page, newPerPage);
  };

  // const handleGenerate = () => {
  //   setCurrentPage(1);
  //   fetchData(1, pageSize);
  // };
  useEffect(() => {
    fetchData();
  }, [
    filterText,
    selectedCategory,
    fromDate,
    toDate,
    selectedOrderStatus,
    productName,
  ]);
  const getDateRangeDisplay = () => {
    if (!fromDate || !toDate) return "Select Date Range";
    const start = new Date(fromDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = new Date(toDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  };

  // Handle export with date range
  const handleExportWithDateRange = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a valid date range");
      return;
    }

    setExportLoading(true);
    try {
      const startDate = formatLocalDate(fromDate);
      const endDate = formatLocalDate(toDate);

      const response = await SalesReportService.getTransactionExportList({
        OrderStatus: selectedOrderStatus?.value ?? "",
        Salesperson: filterText,
        PaymentMethodId: selectedCategory?.value || id,
        ProductName: productName,
        fromDate: startDate,
        toDate: endDate,
        page: 1,
        pageSize: pageSize,
      });

      const exportData = response?.items || [];

      if (exportData.length === 0) {
        toast.error("No data found for the selected filters");
        setExportLoading(false);
        return;
      }

      const columnMapping = {
        "S.N": (row, index) => index + 1,
        "Invoice No": "invoiceNo",
        "Customer Name": "customerName",
        "Transaction Date": (row) => formatDateTimes(row.transactionDate),
        "Payment Method": "paymentMethod",
        "Item Count": "itemCount",
        Discount: "discount",
        "Net Price": "netAmount",
        "Payment Reference": "paymentReference",
        "Service Charge": "serviceCharge",
        Tax: "tax",
        "Gross Amount": "grossAmount",
      };

      exportToExcel(
        exportData,
        columnMapping,
        `TransactionReport_${startDate}_to_${endDate}.xlsx`,
      );

      toast.success("Export successful!");
      setExportLoading(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  // Default: last 1 month on mount + auto-fetch
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    setDateRange([lastMonth, today]);

    // Fetch data correctly with default page and pageSize
    fetchData(
      1,
      pageSize,
      lastMonth,
      today,
      filterText,
      selectedCategory,
      selectedOrderStatus,
    );
  }, []);

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Transaction Report"
          pageTitle="Reports"
          breadcrumbItems={breadcrumbItems}
        />

        <div
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
            overflow: "hidden",
          }}
        >
          {/* Main Content */}
          <div className="p-3 p-lg-4 mt-1">
            {/* Filters */}
            <Row className="mb-3 g-2 align-items-center">
              {/* Title & date display */}
              <Col md={3}>
                <div>
                  <h5 className="mb-1 fw-bold text-dark">Transaction Report</h5>
                  <p className="text-muted small mb-0">
                    {getDateRangeDisplay()}
                  </p>
                </div>
              </Col>

              {/* Filters and buttons */}
              <Col md={9}>
                <Row className="g-2 align-items-end">
                  {/* <Col md={3}>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Sales Person"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </InputGroup>
                  </Col> */}
                  <Col md={3}>
                    <Input
                      type="text"
                      placeholder="Product Name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Select
                      isClearable
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={setSelectedCategory}
                      placeholder="Select payment type"
                      className="fs-6"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Col>
                  <Col md={2}>
                    <Select
                      isClearable
                      options={orderStatusOptions}
                      value={selectedOrderStatus}
                      onChange={setSelectedOrderStatus}
                      placeholder="Select order status"
                      className="fs-6"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Col>
                  <Col md={3}>
                    <InputGroup>
                      <Flatpickr
                        options={{ mode: "range", dateFormat: "Y-m-d" }}
                        value={dateRange}
                        onChange={(dates) => {
                          if (dates.length === 2) {
                            setDateRange(dates);
                            setFromDate(dates[0]);
                            setToDate(dates[1]);
                          }
                        }}
                        placeholder="Select Date Range"
                        className="form-control"
                      />
                      <InputGroupText style={{ cursor: "pointer" }}>
                        <i className="bx bx-calendar"></i>
                      </InputGroupText>
                    </InputGroup>
                  </Col>
                  {/* 
                  <Col md={2}>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={handleGenerate}
                      title="Generate"
                    >
                      <i className="bx bx-search-alt me-1"></i>
                    </Button>
                  </Col> */}

                  <Col md={1}>
                    <Button
                      color="success"
                      className="w-100"
                      onClick={handleExportWithDateRange}
                      disabled={exportLoading}
                      title="Export"
                    >
                      <i className="bx bx-export me-1"></i>
                      {/* {exportLoading ? "Exporting..." : "Export"} */}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Summary Footer - Now shows totals from ALL data */}
            {allData.length > 0 && (
              <div
                className="p-3 mt-4 mb-3"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "0.375rem",
                  border: "1px solid #dee2e6",
                }}
              >
                <Row className="fw-bold">
                  <Col md={2}>
                    <h6 className="mb-0 text-dark">Total Summary</h6>
                    <small className="text-muted">
                      (All {totalRows} records)
                    </small>
                  </Col>
                  <Col md={10}>
                    <Row>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Total Orders:</span>
                        <span className="text-dark">
                          {summaryTotals.totalOrders}
                        </span>
                      </Col>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Total Discount:</span>
                        <span className="text-dark">
                          <NumberWithCommas
                            number={summaryTotals.totalDiscount}
                          />
                        </span>
                      </Col>

                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Total Tax:</span>
                        <span className="text-dark">
                          <NumberWithCommas number={summaryTotals.totalTax} />
                        </span>
                      </Col>
                      <Col md={3} className="text-end">
                        <span className="text-muted me-2">Gross Amount:</span>
                        <span className="text-dark">
                          <NumberWithCommas
                            number={summaryTotals.totalGrossAmount}
                          />
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            )}
            {/* Table */}
            <CustomDataTable
              responsive
              striped
              pagination
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={pageSize}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              fixedHeader
              highlightOnHover
              pointerOnHover
              persistTableHead
              progressPending={loading}
              paginationServer
              columns={columns}
              data={allData}
              customStyles={{
                headCells: {
                  style: {
                    fontSize: "0.92rem",
                    fontWeight: 610,
                  },
                },
                rows: {
                  style: {
                    fontSize: "0.9rem",
                  },
                },
              }}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle visible height="80" width="80" color="#5B71B9" />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">No Data Found</h5>
                  <p className="text-muted mb-0">
                    No transaction data available for the selected filters.
                  </p>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default TransactionReport;
